'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  Sparkles,
  Zap,
  VolumeX,
  Armchair,
  Tv,
  Sun,
  Shield,
  Gauge,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = 'Toyota Alphard Gen 4 (HEV)') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan ketersediaan layanan Sewa Alphard Tangerang untuk unit ${vehicle}.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Sopir / VIP Service
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai:
• Ketersediaan unit Alphard HEV
• Harga dan pilihan paket VIP
• Fasilitas yang didapat
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

// Fitur Unggulan Utama Toyota Alphard HEV
const alphardHighlights = [
  {
    icon: Armchair,
    title: 'Executive Ottoman Seats',
    description: 'Jok kulit elektrik baris kedua dilengkapi penopang betis (leg rest), pemanas, pendingin, dan fungsi pijat untuk kenyamanan maksimal.',
  },
  {
    icon: VolumeX,
    title: 'Kabin Super Senyap (EV Mode)',
    description: 'Teknologi Hybrid Electric Vehicle memberikan kesenyapan akustik kabin tingkat tinggi, bebas dari bising suara mesin.',
  },
  {
    icon: Sun,
    title: 'Dual Sunroof & Ambient Light',
    description: 'Atap kaca ganda dengan sistem pencahayaan warna interior yang dapat disesuaikan untuk menciptakan suasana mewah.',
  },
  {
    icon: Tv,
    title: 'JBL Audio & Rear Entertainment',
    description: 'Sistem tata suara premium JBL 15-speaker dan layar hiburan kabin belakang untuk pengalaman multimedia kelas atas.',
  },
  {
    icon: Shield,
    title: 'Toyota Safety Sense (TSS 3.0)',
    description: 'Perlindungan aktif tingkat tinggi mencakup Adaptive Cruise Control, Lane Tracing Assist, dan Pre-Collision System.',
  },
  {
    icon: Gauge,
    title: 'Mesin Hybrid 2.5L Efisien',
    description: 'Kombinasi sempurna antara performa mesin bensin 2.5L bertenaga tinggi dan efisiensi konsumsi bahan bakar EV.',
  },
];

// Khusus menampilkan varian Toyota Alphard saja sesuai permintaan
const fleet = [
  {
    name: 'Toyota Alphard Gen 4 (HEV)',
    category: 'Ultra Luxury MPV',
    status: 'Tersedia',
    transmission: 'CVT Matic',
    fuel: 'Bensin / Hybrid (EV Mode)',
    capacity: '5 Penumpang VIP',
    image: '/assets/images/ALPHARD HEV.jpeg',
    features: [
      'Ottoman Exec Seat',
      'Dual Sunroof',
      'JBL Sound System',
      'Rear Seat Ent.',
      'Wireless Charger',
      'Toyota Safety Sense',
    ],
    price: 'Rp 4.200.000',
    priceLabel: '12 jam · dengan driver VIP',
    description: 'Generasi ke-4 All New Toyota Alphard Hybrid. Standar kemewahan tertinggi dengan kabin kedap dan teknologi futuristik.',
  },
  {
    name: 'Toyota Alphard Facelift Transformer',
    category: 'Luxury MPV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Bensin Premium',
    capacity: '5 Penumpang VIP',
    image: '/assets/images/Alphard Facelift.jpeg',
    features: [
      'Captain Seat Ottoman',
      'Sunroof / Moonroof',
      'Ambient Interior Light',
      'AC Multi Zone',
      'Power Back Door',
    ],
    price: 'Rp 3.000.000',
    priceLabel: '12 jam · dengan driver VIP',
    description: 'Varian Alphard Facelift favorit untuk penjemputan tamu VVIP di Bandara Soetta, Wedding Car, dan perjalanan direksi.',
  },
];

const services = [
  {
    icon: Plane,
    title: 'VVIP Airport Transfer Soekarno-Hatta',
    description:
      'Penjemputan eksklusif tepat di pintu Terminal 3 VIP Bandara Soekarno-Hatta dengan Toyota Alphard HEV senyap & nyaman.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Direksi & Tamu VVIP',
    description:
      'Solusi kendaraan berkelas untuk rapat direksi, kunjungan investor, serta event korporat di BSD Green Office Park & Alam Sutera.',
  },
  {
    icon: Sparkles,
    title: 'Sewa Mobil Pengantin (Wedding Car)',
    description:
      'Jadikan momen pernikahan Anda lebih elegan di Tangerang dengan Alphard HEV lengkap hiasan pita bunga dan driver berjas rapi.',
  },
  {
    icon: Users,
    title: 'Sewa Alphard HEV dengan Driver VIP',
    description:
      'Pengemudi terlatih protokoler, berseragam rapi, tepat waktu, dan sangat menjaga privasi perjalanan Anda.',
  },
  {
    icon: Route,
    title: 'Carter VIP Luar Kota via Tol Trans-Jawa',
    description:
      'Perjalanan bisnis jarak jauh (Bandung, Cirebon, Semarang, Surabaya) yang rileks dan tenang berkat kabin bertipe First Class.',
  },
  {
    icon: Building2,
    title: 'Sewa Kontrak Korporat / Harian',
    description:
      'Persewaan mobilisasi eksekutif untuk jangka pendek harian maupun kontrak operasional perusahaan jangka panjang.',
  },
];

const areaGroups = [
  {
    title: 'Kawasan Perkantoran & MICE',
    description: 'Pusat bisnis dan pameran internasional.',
    places: [
      'ICE BSD City',
      'BSD Green Office Park',
      'Alam Sutera Business District',
      'Gading Serpong Commercial Area',
      'CBD Karawaci',
      'Kawasan Industri Jatake',
    ],
  },
  {
    title: 'Kota Tangerang & Selatan',
    description: 'Area hunian & pusat pemerintahan.',
    places: [
      'Tangerang Kota (Puspem)',
      'Tangerang Selatan (BSD)',
      'Serpong Utama',
      'Ciputat & Pamulang',
      'Bintaro Jaya',
      'Karang Tengah & Ciledug',
    ],
  },
  {
    title: 'Kabupaten Tangerang',
    description: 'Menjangkau kawasan berkembang.',
    places: [
      'Lippo Karawaci',
      'Cikupa & Balaraja',
      'Citra Raya Panongan',
      'Pasar Kemis',
      'Teluknaga & Kosambi',
      'Sepatan',
    ],
  },
  {
    title: 'Akses Utama & Bandara',
    description: 'Hub transportasi udara & tol.',
    places: [
      'Bandara Soekarno-Hatta (T1, T2, T3 VIP)',
      'Tol Sedyatmo / Airport Toll',
      'Tol Jakarta - Tangerang',
      'Tol Kunciran - Serpong',
      'Stasiun Tangerang / Batu Ceper',
      'Akses Tol Serpong - Balaraja',
    ],
  },
];

const reasons = [
  {
    icon: Zap,
    title: 'Teknologi Hybrid HEV Terbaru',
    text: 'Mesin Hybrid Electric Vehicle menghadirkan pengalaman berkendara super senyap, halus, dan sangat efisien.',
  },
  {
    icon: Star,
    title: 'Kabin First-Class Ottoman Seat',
    text: 'Fasilitas kursi eksekutif elektrik dengan penopang kaki, audio JBL premium, serta suasana interior mewah.',
  },
  {
    icon: ShieldCheck,
    title: 'Driver Standar VIP / Protocol',
    text: 'Pengemudi terlatih dengan etiket pelayanan eksklusif, rapi, ramah, dan sangat menjaga privasi pelanggan.',
  },
  {
    icon: MessageCircle,
    title: 'Pemesanan Praktis & Responsif',
    text: 'Layanan reservasi cepat 24 jam via WhatsApp untuk memastikan unit Alphard HEV siap sesuai jadwal.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Teknologi & Fitur',
    title: 'Keunggulan Sewa All New Toyota Alphard HEV (Gen 4) di Tangerang',
    paragraphs: [
      'All New Toyota Alphard HEV (Hybrid Electric Vehicle) Gen 4 menetapkan standar baru dalam lini MPV mewah. Dengan mengombinasikan mesin bensin 2.5L dan motor listrik canggih, mobil ini memberikan keheningan kabin luar biasa terutama saat melaju di kecepatan rendah menggunakan EV mode.',
      'Bagi para pejabat, direksi perusahaan, maupun tamu VVIP yang membutuhkan ketenangan ekstra selama perjalanan dari Bandara Soekarno-Hatta menuju BSD City atau Jakarta, kesenyapan Alphard HEV memberikan suasana ideal untuk beristirahat atau bekerja.',
      'Sistem suspensi adaptif terbarunya juga mampu meredam getaran dengan sempurna, menjadikan setiap perjalanan melintasi Tol Sedyatmo atau Tol Jakarta-Tangerang terasa sangat halus.',
    ],
  },
  {
    number: '02',
    category: 'VIP Airport Transfer',
    title: 'Layanan Antar Jemput VVIP Bandara Soekarno-Hatta Menggunakan Alphard HEV',
    paragraphs: [
      'Bandara Internasional Soekarno-Hatta (CGK) di Tangerang adalah pintu masuk utama para eksekutif internasional, investor, dan tokoh penting. Layanan VIP Airport Transfer dari NAYVERA dirancang khusus untuk penyambutan berkelas sejak titik kedatangan Terminal 3 VIP.',
      'Driver VIP kami berseragam resmi akan bersiap tepat waktu, mengurus bagasi dengan cermat, dan memastikan perjalanan menuju destinasi utama Anda berjalan aman tanpa hambatan.',
      'Armada Toyota Alphard HEV kami selalu dalam kondisi higienis, harum, dan siap menyajikan kenyamanan terbaik bagi tamu kehormatan Anda.',
    ],
  },
  {
    number: '03',
    category: 'Korporat & MICE',
    title: 'Solusi Mobilitas Direksi & Tamu Pameran di ICE BSD Tangerang Selatan',
    paragraphs: [
      'Sebagai pusat pameran dan konvensi terbesar di Indonesia, ICE BSD City rutin menyelenggarakan pameran otomotif skala global, konser musik internasional, dan KTT korporat.',
      'NAYVERA INDO TRANSPORT menyediakan unit Alphard HEV khusus untuk mobilisasi jajaran direksi, pembicara internasional, hingga performer VIP.',
      'Dengan memesan unit Alphard HEV di NAYVERA, Anda dijamin mendapatkan unit rakitan terbaru dengan perawatan berkala resmi Toyota serta pengemudi yang sangat paham akses-akses jalan VIP.',
    ],
  },
  {
    number: '04',
    category: 'Wedding Car',
    title: 'Mobil Pengantin Mewah Toyota Alphard HEV untuk Pernikahan Istimewa',
    paragraphs: [
      'Pernikahan adalah momen berharga sekali seumur hidup yang memerlukan kenyamanan sarana transportasi terbaik bagi pasangan pengantin.',
      'Toyota Alphard HEV warna putih atau hitam menjadi pilihan utama Wedding Car di hotel bintang lima dan hall megah area Serpong dan Alam Sutera. Desain grill depan yang gagah memberikan aksen glamor pada dokumentasi pernikahan Anda.',
      'Paket sewa Wedding Car Alphard HEV NAYVERA sudah mencakup dekorasi bunga anggun dan driver profesional yang siap mendampingi pasangan pengantin seharian.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa tarif sewa Toyota Alphard Gen 4 (HEV) di Tangerang bersama NAYVERA?',
    answer:
      'Tarif sewa All New Toyota Alphard Gen 4 (HEV Hybrid) di Tangerang mulai dari Rp 4.200.000 per 12 jam, sudah termasuk pengemudi VIP profesional. Tersedia juga paket All-In (Driver, BBM, Tol, Parkir).',
  },
  {
    question: 'Apa saja fasilitas kenyamanan utama di dalam kabin Toyota Alphard HEV?',
    answer:
      'Kabin dilengkapi dengan Executive Ottoman Seats (kursi elektrik dengan leg rest, pemanas, dan pendingin), Dual Sunroof, sistem tata suara JBL 15-speaker, layar hiburan belakang, wireless charger, serta AC Multi-Zone.',
  },
  {
    question: 'Apakah sewa Alphard HEV di NAYVERA sudah termasuk jasa pengemudi?',
    answer:
      'Ya, seluruh pemesanan armada Alphard HEV disewakan bersama driver VIP profesional yang berpengalaman, berpenampilan rapi, sopan, dan menguasai etiket protokoler.',
  },
  {
    question: 'Apakah melayani penjemputan langsung dari Terminal 3 VIP Bandara Soekarno-Hatta?',
    answer:
      'Sangat bisa. Driver kami siap menyambut Anda di seluruh area kedatangan Bandara Soekarno-Hatta, termasuk Terminal 3 VIP dan Lounge Commercial.',
  },
  {
    question: 'Bagaimana cara konfirmasi reservasi unit Alphard HEV?',
    answer:
      'Cukup hubungi WhatsApp kami di 087786556067, sampaikan tanggal pemakaian, lokasi penjemputan di Tangerang/Bandara, dan admin kami akan segera menerbitkan bukti pemesanan.',
  },
];

export default function SewaMobilAlphardHevTangerangSubPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0">
          <img
            src="/assets/images/ALPHARD HEV.jpeg"
            alt="Sewa mobil Toyota Alphard HEV Tangerang NAYVERA INDO TRANSPORT"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <Zap size={16} className="text-[#e4c45a]" />
              Sewa Mobil Toyota Alphard HEV (Hybrid) Tangerang
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Alphard HEV
              <span className="mt-2 block text-[#d4af37]">
                Tangerang VIP Executive Class
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              Nikmati kemewahan dan kesenyapan berkendara tingkat tinggi bersama All New Toyota Alphard Gen 4 (HEV Hybrid). 
              Layanan eksklusif untuk penjemputan VVIP Bandara Soekarno-Hatta, pertemuan bisnis BSD City, hingga Wedding Car.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi & Booking WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#highlight-unit"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Eksplor Fitur Alphard HEV
              </a>
            </div>

            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['4.2 Jutaan', 'Tarif VIP 12 Jam*'],
                ['Hybrid EV', 'Kabin Super Senyap'],
                ['Driver VIP', 'Layanan Eksekutif'],
                ['Soetta & BSD', 'Penjemputan Cepat'],
              ].map(([value, label]) => (
                <div key={label} className="border-l border-[#d4af37]/50 pl-4">
                  <div className="font-bold text-[#e4c45a]">{value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-400">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Sparkles size={25} />
                </div>

                <div>
                  <p className="font-bold text-white">Butuh Alphard HEV di Tangerang?</p>
                  <p className="text-sm text-slate-400">Reservasi cepat untuk tamu VIP Anda</p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi sewa',
                  'Pilih rute (Soetta, BSD, atau Luar Kota)',
                  'Driver VIP berseragam & profesional',
                  'Kabin senyap dengan Ottoman Seat',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 size={19} className="mt-0.5 shrink-0 text-[#d4af37]" />
                    <p className="text-sm leading-6 text-slate-300">{item}</p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Cek Ketersediaan Unit HEV
              </a>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Layanan responsif 24 Jam oleh tim NAYVERA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHT DETAILS UNIT ALPHARD HEV */}
      <section id="highlight-unit" className="bg-slate-900 py-20 text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Spesifikasi & Keunggulan
            </p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
              Detail Kemewahan Toyota Alphard HEV
            </h2>
            <p className="mt-4 text-slate-400">
              Dirancang untuk memberikan standar berkendara kelas satu dengan keheningan dan kenyamanan terbaik di kelasnya.
            </p>
          </div>

          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {alphardHighlights.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/15 text-[#e4c45a]">
                    <Icon size={24} />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-white">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-400">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KATALOG UNIT TOYOTA ALPHARD */}
      <section id="armada" className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Lini Toyota Alphard
              </p>

              <h2 className="text-3xl font-bold text-[#07111f] sm:text-4xl">
                Pilihan Varian Toyota Alphard
                <span className="text-[#b89528]"> NAYVERA</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Armada Alphard kami selalu dalam kondisi paling prima, bebas bau rokok, dan rutin diservis di bengkel resmi Toyota.
              </p>
            </div>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:border-[#d4af37] hover:shadow-xl"
              >
                <div>
                  <div className="relative aspect-[16/9] overflow-hidden rounded-2xl bg-slate-100">
                    <img
                      src={car.image}
                      alt={`${car.name} sewa mobil Alphard HEV Tangerang NAYVERA`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      {car.status}
                    </div>

                    <div className="absolute right-3 top-3 rounded-full bg-[#d4af37] px-3 py-1 text-xs font-bold text-[#07111f] shadow-sm">
                      {car.category}
                    </div>

                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                      <Users size={14} className="text-[#d4af37]" />
                      {car.capacity}
                    </div>
                  </div>

                  <div className="mt-5">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#b89528]">
                      {car.category}
                    </span>
                    <h3 className="mt-1 text-2xl font-bold text-[#07111f]">{car.name}</h3>
                    <p className="mt-2 text-sm text-slate-600">{car.description}</p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚙️ {car.transmission}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚡ {car.fuel}
                    </span>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600 font-medium"
                      >
                        ✨ {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-8 border-t border-slate-100 pt-5">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Mulai dari</p>
                      <p className="text-2xl font-black text-[#07111f]">{car.price}</p>
                      <p className="text-xs text-slate-400">{car.priceLabel}</p>
                    </div>

                    <div className="flex gap-2">
                      <a
                        href={`/booking-page?vehicle=${encodeURIComponent(car.name)}`}
                        className="inline-flex items-center justify-center rounded-xl bg-[#d4af37] px-5 py-2.5 text-xs font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
                      >
                        Booking
                      </a>
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-emerald-500/40 bg-emerald-50/50 px-4 py-2.5 text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                      >
                        <MessageCircle size={14} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan Eksekutif
            </p>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Paket Sewa Alphard HEV
              <span className="text-[#d4af37]"> Terfavorit</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <article
                  key={service.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a]">
                    <Icon size={23} />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-white">{service.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-400">{service.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE AREA SECTION */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Cakupan Penjemputan
            </p>
            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Area Penjemputan Alphard HEV
              <span className="text-[#d4af37]"> Tangerang Raya</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article key={area.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>
                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li key={place} className="border-b border-white/5 pb-2 text-sm text-slate-400 last:border-0">
                      {place}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SEO ARTICLES SECTION */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Wawasan & Panduan
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Artikel Seputar
              <span className="text-[#b89528]"> Toyota Alphard HEV</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {articles.map((art) => (
              <article key={art.number} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#07111f] px-3.5 py-1 text-xs font-bold text-[#e4c45a]">
                    {art.category}
                  </span>
                  <span className="text-2xl font-black text-slate-300">{art.number}</span>
                </div>
                <h3 className="mt-5 text-xl font-bold text-[#07111f]">{art.title}</h3>
                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {art.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">FAQ</p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">Pertanyaan yang Sering Diajukan</h2>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm open:border-[#d4af37]">
                <summary className="flex cursor-pointer items-center justify-between font-bold text-[#07111f]">
                  {faq.question}
                  <ChevronDown size={18} className="transition group-open:rotate-180 text-[#b89528]" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-slate-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER BANNER */}
      <section className="bg-[#07111f] py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            Siap Menikmati Perjalanan VIP?
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Pesan Toyota Alphard HEV Tangerang Sekarang
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Dapatkan pengalaman berkendara paling senyap, mewah, dan berkelas bersama NAYVERA INDO TRANSPORT.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-8 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
            >
              <MessageCircle size={20} />
              Hubungi WhatsApp VIP Line
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}